import { QueryKeys } from "@/constants/Query";
import { withMultipleEncountersHandler } from "@/features/Patient/MedicalRecords/MedicalHistory/useMedicalHistoryErrorHandler";
import type { ApiError } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { useQueryClient } from "@tanstack/react-query";
import type { Encounter } from "fhir/r4";

export interface RemoveDiagnosisFromVisitParams {
  conditionId: string;
}

interface RemoveDiagnosisFromVisitResponse {
  encounter: Encounter;
}

export interface DiagnosisIdentifiers {
  conditionId?: string;
  snomedCode?: string;
}

export const useRemoveDiagnosisFromVisit = ({
  patientId,
  motherEncounterId,
  onSuccess,
  onError,
  onSettled,
}: {
  patientId: string;
  motherEncounterId: string;
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
  onSettled?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<
    RemoveDiagnosisFromVisitResponse,
    RemoveDiagnosisFromVisitParams
  >({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Conditions.REMOVE_DIAGNOSIS({
        patientId,
        encounterId: motherEncounterId,
      }),
      mutationFn: ({ conditionId }) =>
        axiosInstance.post(
          `/charting/encounter-charting/${motherEncounterId}/diagnoses/remove`,
          { conditionIds: [conditionId], snomedCodes: [] },
        ),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Conditions.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Conditions.Search.DEFAULT({
            patientId,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Distribution.AVAILABLE_ACTIONS({
            patientId,
            encounterId: motherEncounterId,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Timeline.ENCOUNTERS({
            patientId,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.EpisodesOfCare.LIST(patientId),
        });
        onSuccess?.();
      },
      onError: withMultipleEncountersHandler(onError),
      onSettled,
    },
  });
};
